import { useAppDispatch, useAppSelector } from "app/hooks";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Outlet } from "react-router-dom";
import { DashboardMenu } from "./dashboard-menu/DashboardMenu";
import { getAllReferentials } from "features/referentials/slices/getAllReferentialsThunk";
import MobileMenu from "./dashboard-menu/MobileMenu";
import { Menu } from "lucide-react";
import { ReferentialKey } from "api/referential";
import { LeadsArrivalGate } from "features/notifications";

const DashboardLayout: React.FC = () => {
  const auth = useAuth();
  const dispatch = useAppDispatch();

  // État local pour tracker si on a déjà tenté de charger les référentiels
  const [referentialsLoadAttempted, setReferentialsLoadAttempted] = useState(false);
  const [referentialsError, setReferentialsError] = useState<string | null>(null);
  const [themeError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Sélectionner les référentiels depuis le store Redux
  const referentials = useAppSelector(state => state.referential.referentials);
  const referentialsLoading = useAppSelector(state => state.referential.loading);

  // Fonction pour vérifier si les référentiels sont vides ou incomplets
  const areReferentialsEmpty = useMemo(() => {
    const requiredKeys = [
      ReferentialKey.Civility,
      ReferentialKey.LegalForm,
      ReferentialKey.Nationality,
      ReferentialKey.Country,
      ReferentialKey.CompanyRole
    ];
    return requiredKeys.some(key =>
      !referentials[key] || referentials[key].length === 0
    );
  }, [referentials]);

  // Effet séparé pour les référentiels avec gestion d'erreur et retry
  useEffect(() => {
    const loadReferentials = async () => {
      if (retryCount >= MAX_RETRIES) {
        setReferentialsError('Impossible de charger les données de référence après plusieurs tentatives');
        return;
      }

      try {
        await dispatch(getAllReferentials()).unwrap();
        setReferentialsError(null);
        setRetryCount(0);
      } catch (error) {
        console.error('Erreur lors du chargement des référentiels:', error);
        setReferentialsError('Erreur de connexion au serveur');
        setRetryCount(prev => prev + 1);

        // Retry automatique avec délai croissant
        if (retryCount < MAX_RETRIES - 1) {
          setTimeout(() => {
            setReferentialsLoadAttempted(false);
          }, Math.pow(2, retryCount) * 1000); // Délai exponentiel : 1s, 2s, 4s
        }
      }
    };

    if (auth.isAuthenticated &&
      !referentialsLoadAttempted &&
      areReferentialsEmpty &&
      !referentialsLoading) {
      setReferentialsLoadAttempted(true);
      loadReferentials();
    }
  }, [auth.isAuthenticated, referentialsLoadAttempted, areReferentialsEmpty, referentialsLoading, dispatch, retryCount]);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(false);

  // Fonction pour retry manuel
  const handleRetryReferentials = () => {
    setReferentialsError(null);
    setReferentialsLoadAttempted(false);
    setRetryCount(0);
  };

  // Afficher les erreurs de manière non-bloquante
  const renderErrorBanner = () => {
    if (!referentialsError && !themeError) return null;

    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              {referentialsError && (
                <span>Problème de connexion détecté. Certaines fonctionnalités peuvent être limitées. </span>
              )}
              {themeError && (
                <span>Impossible de charger vos préférences. </span>
              )}
              <button
                onClick={handleRetryReferentials}
                className="font-medium underline text-yellow-700 hover:text-yellow-600"
              >
                Réessayer
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Leads partenaires adressés au courtier depuis sa dernière visite : modale
          d'accueil, une fois par session. N'affiche rien s'il n'y a rien. */}
      <LeadsArrivalGate />
      <MobileMenu
        isCollapsed={isMobileCollapsed}
        onClose={() => setIsMobileCollapsed(false)}
      />
      <DashboardMenu
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(!isCollapsed)}
      />
      <div className={`bg-primary/10 h-screen overflow-auto flex flex-col flex-1 ${isCollapsed ? 'md:pl-24' : 'md:pl-56'}`}>
        <header className="sticky mb-2 top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-primary border">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            onClick={() => setIsMobileCollapsed(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="text-white h-6 w-6" aria-hidden="true" />
          </button>
        </header>

        {/* Banner d'erreur non-bloquant */}
        <div className="px-4">
          {renderErrorBanner()}
        </div>

        <main className="relative flex-1">
          <Outlet />
        </main>
      </div>
    </>
  );
};

export default DashboardLayout;
